import { useIsUnderwriterOrAdminUser } from '@/shared/hooks';
import { Text, Tooltip } from '@noorden-insurtech/alba-design-system';
import { Briefcase, ChevronDown, ChevronUp, Info, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type {
  CalculationCommercialization,
  CalculationCoverageGroup,
  CalculationDataByStipulant,
  CalculationInstallmentRow,
  CalculationSimpleGroup,
  CalculationStipulantMetric,
  CalculationSummaryCard,
  StipulantAccordionEntry,
} from '../step-calculation.types';

// ─── CalculationInstallmentsTableSection ────────────────────────────────────

type InstallmentsTableProps = {
  items: CalculationInstallmentRow[];
  blurPremiumValues?: boolean;
  showUnavailableMessage?: boolean;
};

export function CalculationInstallmentsTableSection({
  items,
  blurPremiumValues = false,
  showUnavailableMessage = false,
}: InstallmentsTableProps) {
  if (!items.length) return null;

  const premiumClass = blurPremiumValues ? 'blur-[6px] select-none' : '';

  return (
    <div className='relative flex w-full max-w-[683px] flex-col overflow-hidden rounded-3xl border border-alba-gray-300 bg-alba-white-500 shadow-xl'>
      <div className='flex items-center bg-alba-gray-200 px-12 py-4'>
        <Text className='flex-1 text-xs font-bold text-alba-black-500'>
          Periodicidade
        </Text>
        <Text className='flex-1 text-center text-xs font-bold text-alba-black-500'>
          Valor do Prêmio
        </Text>
      </div>

      {items.map((row, index) => (
        <div key={row.periodicity} className='flex flex-col'>
          {index > 0 ? <div className='h-px w-full bg-alba-gray-300' /> : null}
          <div className='flex items-center px-12 py-4'>
            <Text className='flex-1 text-sm text-alba-black-500'>
              {row.periodicity}
            </Text>
            <Text
              className={`flex-1 text-center text-sm text-alba-black-500 ${premiumClass}`}
            >
              {row.premiumValue}
            </Text>
          </div>
        </div>
      ))}

      {showUnavailableMessage && (
        <div className='border-t border-alba-gray-300 bg-alba-white-500 px-6 py-4 text-center'>
          <Text className='text-sm font-semibold text-[#F46363]'>
            Cálculo indisponível — cotação em análise da subscrição
          </Text>
        </div>
      )}
    </div>
  );
}

// ─── CalculationSummaryCardSection ──────────────────────────────────────────

type SummaryCardProps = {
  data: CalculationSummaryCard;
};

export function CalculationSummaryCardSection({ data }: SummaryCardProps) {
  if (!data.items.length) return null;

  return (
    <div className='flex w-full flex-col gap-4'>
      <Text className='text-base text-alba-black-500'>{data.title}</Text>
      <div className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'>
        {data.items.map((item, index) => (
          <div key={item.label} className='flex flex-col gap-2'>
            {index > 0 ? <div className='h-px w-full bg-alba-gray-200' /> : null}
            <div className='flex items-center justify-between gap-4'>
              <Text className='text-sm font-bold text-alba-black-500'>
                {item.label}
              </Text>
              <Text className='text-sm text-alba-gray-600'>{item.value}</Text>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Card "Dados do Cálculo": o consolidado do grupo (estipulante +
 * subestipulantes), não do estipulante principal — daí o tooltip no título, que
 * evita a leitura errada de que o quadro seria só da empresa do cabeçalho.
 *
 * Restrito a subscritor e administrador, junto com o detalhamento por entidade
 * da CalculationDataByStipulantSection: os dois vêm da base de cadastro, não do
 * que o corretor informou na cotação, então ficam fora da visão dos demais
 * perfis.
 */
export function CalculationDataCardSection({ data }: SummaryCardProps) {
  const { isUnderwriterOrAdminUser } = useIsUnderwriterOrAdminUser();

  if (isUnderwriterOrAdminUser !== true) return null;
  if (!data.items.length) return null;

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='flex items-center gap-1.5'>
        <Text className='text-base text-alba-black-500'>{data.title}</Text>
        <Tooltip.Root
          openDelay={100}
          closeDelay={150}
          positioning={{ placement: 'top' }}
        >
          <Tooltip.Trigger asChild>
            <button
              type='button'
              aria-label='Como os dados do cálculo são somados'
              className='inline-flex cursor-pointer text-alba-gray-500 transition-colors hover:text-alba-gray-600'
            >
              <Info className='h-4 w-4' strokeWidth={1.6} />
            </button>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Positioner>
              <Tooltip.Content className='max-w-[280px] leading-snug'>
                Os valores deste resumo são calculados com base nas informações
                de todos os estipulantes e subestipulantes. Esses volumes se
                referem à base de cadastro, e não ao que está sendo informado na
                cotação.
              </Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Portal>
        </Tooltip.Root>
      </div>
      <div className='grid grid-cols-1 divide-y divide-alba-gray-200 rounded-xl border border-alba-gray-400 sm:grid-cols-2 sm:divide-x sm:divide-y-0'>
        {data.items.map(item => (
          <div
            key={item.label}
            className='flex items-center justify-between gap-4 p-4 even:border-t even:border-alba-gray-200 sm:even:border-t-0 sm:nth-[n+3]:border-t sm:nth-[n+3]:border-alba-gray-200'
          >
            <Text className='text-sm font-bold text-alba-black-500'>
              {item.label}:
            </Text>
            <Text className='text-sm font-bold text-alba-primary-500'>
              {item.value}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CalculationDataByStipulantSection ──────────────────────────────────────

type StipulantMetricGroupProps = {
  icon: ReactNode;
  title: string;
  metric: CalculationStipulantMetric;
};

function StipulantMetricGroup({ icon, title, metric }: StipulantMetricGroupProps) {
  return (
    <div className='flex flex-col gap-2 rounded-lg border border-alba-gray-200 p-3'>
      <div className='flex items-center gap-2'>
        <span className='text-alba-primary-500'>{icon}</span>
        <Text className='text-sm font-bold text-alba-black-500'>{title}</Text>
      </div>
      <div className='flex items-center justify-between gap-4'>
        <Text className='text-xs text-alba-gray-600'>Quantidade</Text>
        <Text className='text-sm font-bold text-alba-black-500'>{metric.count}</Text>
      </div>
      <div className='flex items-center justify-between gap-4'>
        <Text className='text-xs text-alba-gray-600'>Idade média</Text>
        <Text className='text-sm font-bold text-alba-black-500'>
          {metric.averageAge}
        </Text>
      </div>
    </div>
  );
}

type DataByStipulantProps = {
  entries: CalculationDataByStipulant[];
};

/**
 * "Janela" recolhível com sócios e colaboradores por estipulante/sub. Restrita a
 * subscritor e administrador — mesmo par de perfis do gate de inspeção da página
 * de Apólices, e o mesmo do card "Dados do Cálculo", que some junto. `=== true`
 * mantém oculto enquanto a checagem de grupo não resolve, para o bloco nunca
 * piscar na tela de quem não pode vê-lo.
 */
export function CalculationDataByStipulantSection({
  entries,
}: DataByStipulantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { isUnderwriterOrAdminUser } = useIsUnderwriterOrAdminUser();

  if (isUnderwriterOrAdminUser !== true) return null;
  if (!entries.length) return null;

  return (
    <div className='flex w-full flex-col overflow-hidden rounded-xl border border-alba-gray-400'>
      <button
        type='button'
        className='flex w-full items-center justify-between gap-4 px-4 py-4 text-left'
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
      >
        <div className='flex items-center gap-2'>
          <Text className='text-sm font-bold text-alba-black-500'>
            Funcionários e sócios por estipulante
          </Text>
          <span className='inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-alba-gray-400 px-1.5 text-xs font-bold text-alba-gray-600'>
            {entries.length}
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className='h-5 w-5 shrink-0 text-alba-gray-600' strokeWidth={1.6} />
        ) : (
          <ChevronDown className='h-5 w-5 shrink-0 text-alba-gray-600' strokeWidth={1.6} />
        )}
      </button>

      {isOpen ? (
        <div className='flex flex-col gap-3 border-t border-alba-gray-400 px-4 py-4'>
          {entries.map(entry => (
            <div
              key={`${entry.roleLabel}-${entry.name}`}
              className='flex flex-col gap-3 rounded-xl border border-alba-gray-400 p-4'
            >
              <div className='flex flex-wrap items-center gap-2'>
                <span className='rounded-md border border-alba-gray-400 px-2 py-0.5 text-xs font-bold text-alba-gray-600'>
                  {entry.roleLabel}
                </span>
                {entry.name ? (
                  <Text className='text-sm font-bold text-alba-black-500'>
                    {entry.name}
                  </Text>
                ) : null}
              </div>

              <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                <StipulantMetricGroup
                  icon={<Users className='h-4 w-4' strokeWidth={1.6} />}
                  title='Funcionários'
                  metric={entry.employees}
                />
                <StipulantMetricGroup
                  icon={<Briefcase className='h-4 w-4' strokeWidth={1.6} />}
                  title='Sócios'
                  metric={entry.partners}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── CalculationCoveragesSection ────────────────────────────────────────────

type CoveragesSectionProps = {
  groups: CalculationCoverageGroup[];
};

export function CalculationCoveragesSection({ groups }: CoveragesSectionProps) {
  if (!groups.length) return null;

  return (
    <div className='flex flex-col gap-6'>
      <Text className='text-base text-alba-black-500'>Coberturas</Text>
      <div className='flex flex-col gap-4'>
        {groups.map(group => (
          <div
            key={group.title}
            className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'
          >
            <Text className='text-sm font-bold text-alba-black-500'>
              {group.title}
            </Text>
            <div className='h-px w-full bg-alba-gray-200' />
            {group.items.map(item => (
              <div
                key={`${group.title}-${item.name}`}
                className='flex items-start justify-between gap-4'
              >
                <Text className='flex-1 text-xs text-alba-gray-600'>{item.name}</Text>
                <div className='flex flex-1 items-center justify-end gap-8'>
                  <Text className='flex-1 text-right text-xs text-alba-gray-600'>
                    {item.capital}
                  </Text>
                  {item.percent ? (
                    <Text className='shrink-0 whitespace-nowrap text-xs text-alba-gray-600'>
                      {item.percent}
                    </Text>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CalculationAssistancesCapitalizationSection ────────────────────────────

type AssistancesCapitalizationProps = {
  groups: CalculationSimpleGroup[];
};

export function CalculationAssistancesCapitalizationSection({
  groups,
}: AssistancesCapitalizationProps) {
  if (!groups.length) return null;

  return (
    <div className='flex flex-col gap-6'>
      <Text className='text-base text-alba-black-500'>
        Assistências e Capitalização
      </Text>
      <div className='flex flex-col gap-4'>
        {groups.map(group => (
          <div
            key={group.title}
            className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'
          >
            <Text className='text-sm font-bold text-alba-black-500'>
              {group.title}
            </Text>
            <div className='h-px w-full bg-alba-gray-200' />
            {group.items.map(item => (
              <Text
                key={`${group.title}-${item}`}
                className='text-xs text-alba-gray-600'
              >
                {item}
              </Text>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CalculationStipulantsListSection ───────────────────────────────────────

type StipulantCardProps = {
  entry: StipulantAccordionEntry;
};

function StipulantAccordionCard({ entry }: StipulantCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const hasContent =
    entry.riskDataCard.items.length > 0 ||
    entry.coverageGroups.length > 0 ||
    entry.assistancesCapitalizationGroups.length > 0;

  return (
    <div className='flex flex-col rounded-xl border border-alba-gray-400 overflow-hidden'>
      <button
        type='button'
        className='flex w-full items-center justify-between px-4 py-4 text-left'
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
      >
        <Text className='text-sm font-bold text-alba-black-500'>
          {entry.label}
        </Text>
        {isOpen ? (
          <ChevronUp className='h-5 w-5 shrink-0 text-alba-gray-600' strokeWidth={1.6} />
        ) : (
          <ChevronDown className='h-5 w-5 shrink-0 text-alba-gray-600' strokeWidth={1.6} />
        )}
      </button>

      {isOpen && hasContent ? (
        <div className='flex flex-col gap-6 border-t border-alba-gray-400 px-4 py-4'>
          {entry.riskDataCard.items.length > 0 ? (
            <div className='flex flex-col gap-4'>
              <Text className='text-sm text-alba-black-500'>
                Dados do Risco
              </Text>
              <div className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'>
                {entry.riskDataCard.items.map((item, index) => (
                  <div key={item.label} className='flex flex-col gap-2'>
                    {index > 0 ? (
                      <div className='h-px w-full bg-alba-gray-200' />
                    ) : null}
                    <div className='flex items-center justify-between gap-4'>
                      <Text className='text-sm font-bold text-alba-black-500'>
                        {item.label}
                      </Text>
                      <Text className='text-sm text-alba-gray-600'>
                        {item.value}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {entry.coverageGroups.length > 0 ? (
            <div className='flex flex-col gap-4'>
              <Text className='text-sm text-alba-black-500'>Coberturas</Text>
              <div className='flex flex-col gap-4'>
                {entry.coverageGroups.map(group => (
                  <div
                    key={group.title}
                    className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'
                  >
                    <Text className='text-sm font-bold text-alba-black-500'>
                      {group.title}
                    </Text>
                    <div className='h-px w-full bg-alba-gray-200' />
                    {group.items.map(item => (
                      <div
                        key={`${group.title}-${item.name}`}
                        className='flex items-start justify-between gap-4'
                      >
                        <Text className='flex-1 text-xs text-alba-gray-600'>
                          {item.name}
                        </Text>
                        <div className='flex flex-1 items-center justify-end gap-8'>
                          <Text className='flex-1 text-right text-xs text-alba-gray-600'>
                            {item.capital}
                          </Text>
                          {item.percent ? (
                            <Text className='shrink-0 whitespace-nowrap text-xs text-alba-gray-600'>
                              {item.percent}
                            </Text>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {entry.assistancesCapitalizationGroups.length > 0 ? (
            <div className='flex flex-col gap-4'>
              <Text className='text-sm text-alba-black-500'>
                Assistências e Capitalização
              </Text>
              <div className='flex flex-col gap-4'>
                {entry.assistancesCapitalizationGroups.map(group => (
                  <div
                    key={group.title}
                    className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'
                  >
                    <Text className='text-sm font-bold text-alba-black-500'>
                      {group.title}
                    </Text>
                    <div className='h-px w-full bg-alba-gray-200' />
                    {group.items.map(item => (
                      <Text
                        key={`${group.title}-${item}`}
                        className='text-xs text-alba-gray-600'
                      >
                        {item}
                      </Text>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

type StipulantsListProps = {
  entries: StipulantAccordionEntry[];
};

export function CalculationStipulantsListSection({
  entries,
}: StipulantsListProps) {
  if (!entries.length) return null;

  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-base text-alba-black-500'>
        Lista de Estipulantes
      </Text>
      <div className='flex flex-col gap-2'>
        {entries.map(entry => (
          <StipulantAccordionCard key={entry.label} entry={entry} />
        ))}
      </div>
    </div>
  );
}

// ─── CalculationCommercializationSection ────────────────────────────────────

type CommercializationSectionProps = {
  data: CalculationCommercialization;
};

export function CalculationCommercializationSection({
  data,
}: CommercializationSectionProps) {
  return (
    <div className='flex flex-col gap-6'>
      <Text className='text-base text-alba-black-500'>
        Comercialização
      </Text>
      <div className='rounded-xl border border-alba-gray-400 p-4'>
        <div className='grid grid-cols-2 gap-3 text-sm lg:grid-cols-4'>
          <CommercializationInfo
            label='Corretagem'
            value={data.brokeragePercent}
          />
          <CommercializationInfo
            label='Agenciamento'
            value={data.agencingPercent}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Mesma apresentação do resumo do PME (label em caixa alta + valor destacado).
 * O grid mantém as 4 colunas do PME para as larguras baterem entre os produtos;
 * o CG não tem agravo nem desconto atuarial, então ocupa só as duas primeiras.
 */
function CommercializationInfo({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div>
      <Text className='text-xs uppercase tracking-wider text-alba-gray-500'>
        {label}
      </Text>
      <Text className='text-sm font-semibold text-alba-black-500'>{value}</Text>
    </div>
  );
}
